import { ChartBase } from 'components/common';
import { LoadingSpinner } from 'components/ui';
import type { ProductReplenishment } from 'store/services/types';
import { useProductRelations } from '.';
import { ReplenishmentSaveSegment } from './ReplenishmentSaveSegment';

export const Replenishment = () => {
  const { replenishment, replenishmentIsLoading } = useProductRelations();

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xl font-bold">Replenishment</p>
          <p className="font-normal text-gray-500 text-segments-13px">
            For consumables, how likely would customers repurchase and how much
            time?
          </p>
        </div>
      </div>
      {replenishmentIsLoading ? (
        <div className="flex items-center justify-center w-full h-segments-516px">
          <LoadingSpinner className="w-16 h-16 text-segments-accent-color-orange" />
        </div>
      ) : replenishment?.histogram.length ? (
        <ReplenishmentChart product={replenishment} />
      ) : (
        <div className="flex items-center justify-center w-full mt-4 rounded-lg h-segments-516px bg-segments-gray-F8">
          <p className="text-gray-500">No product found</p>
        </div>
      )}
    </div>
  );
};

function ReplenishmentChart({
  product,
}: {
  product: ProductReplenishment | undefined;
}) {
  const { selectedProduct } = useProductRelations();

  const data = Object.fromEntries(
    product?.histogram.map((item) => [item.week, item.count]) || []
  );

  const chartData = {
    datasets: [
      {
        label: '',
        backgroundColor: '#125970',
        borderColor: '#125970',
        data,
      },
    ],
  };

  const options = {
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          text: 'Replenishment period (week)',
          display: true,
          font: { size: 13 },
        },
      },
      y: {
        title: {
          text: '# customers',
          display: true,
          font: { size: 13 },
          padding: {
            top: 0,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <div className="mt-4">
      <div className="py-5">
        <div className="flex space-x-12">
          <div className="space-y-1">
            <p className="text-2xl leading-9">
              {product?.bought_once?.toLocaleString()}
            </p>
            <p className="text-black text-opacity-50 text-segments-13px">
              Bought
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl leading-9">
              {product?.replenishment_rate?.toLocaleString('en', {
                style: 'percent',
              })}
            </p>
            <p className="text-black text-opacity-50 text-segments-13px">
              Replenish rate
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl leading-9">
              {product?.median && Math.ceil(product.median)} days
            </p>
            <p className="text-black text-opacity-50 text-segments-13px">
              Median before
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl leading-9">{product?.most_before} days</p>
            <p className="text-black text-opacity-50 text-segments-13px">
              Most occur before
            </p>
          </div>
          <div className="relative flex items-center pl-10 border-l">
            <ReplenishmentSaveSegment productTitle={selectedProduct?.title} />
          </div>
        </div>
        <div className="mt-8">
          <ChartBase type="bar" data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
}
